"use client";

import React, { useState } from "react";
import { BadgeId, BadgeRarityType } from "@/app/@types";
import { useAppStore } from "@/store/useAppStore";
import { badgeSystem } from "@/utils/badgeSystem";
import { gameRules } from "@/utils/gameRules";
import { getContextualReasons } from "@/utils/getContextualReasons";
import { motion, AnimatePresence } from "framer-motion";
import { useAchievementToast } from "../AchievementToast";
import { useAuth } from "@/hooks/useAuth";
import { fireConfetti } from "@/lib/confetti";
import { generateHistogram } from "@/helpers/generateHistogram";
import { FeedbackModal } from "./FeedbackModal";
import { updateUserScore } from "@/lib/user/updateUserScore";
import { markChallengeCompleted } from "@/lib/challenge/markChallengeCompleted";
import { updateUserRank } from "@/lib/user/updateUserRank";
import { awardBadge } from "@/lib/badges/awardBadge";
import { addChallengeActivity } from "@/lib/activity/addActivityToDB";
import { toast } from "react-toastify";

export const ChallengeModal = () => {
  const { user } = useAuth();
  const {
    selectedChallenge,
    showCrowdResults,
    gameMode,
    userAnswer,
    loading,
    userScore,
    badges,
    completedChallenges,
    streak,
    userRank,
    crowdBeats,
    setCrowdBeats,
    setStreak,
    setCompletedChallenges,
    setShowResult,
    setUserRank,
    setNewBadge,
    setBadges,
    updateUserPointsScore,
    setLoading,
    setUserAnswer,
    setSelectedChallenge,
    setShowCrowdResults,
    setCrowdData,
    setLastResult,
    setShowBadgeModal,
  } = useAppStore();
  const { showAchievement } = useAchievementToast();

  // Add state for challenge result modal
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultData, setResultData] = useState<{
    points: number;
    feedback: string;
    beatCrowd: boolean;
    isCorrect?: boolean;
  } | null>(null);

  const checkForNewBadges = () => {
    try {
      (Object.keys(badgeSystem) as BadgeId[]).forEach(async (badgeId) => {
        if (
          !badges.includes(badgeId) &&
          gameRules().badges[badgeId]?.().earned
        ) {
          user?.id && (await awardBadge(user.id, badgeId));
          setBadges([...badges, badgeId]);
          setNewBadge(badgeSystem[badgeId]);
          setShowBadgeModal(true);

          // Add bonus points for earning badge
          const badgePoints = badgeSystem[badgeId].points;
          updateUserPointsScore(badgePoints);

          // Update leaderboard if user is authenticated
          user?.id && (await updateUserScore(user.id, badgePoints));
          await addChallengeActivity(badgePoints);

          // Show achievement toast
          const badge = badgeSystem[badgeId];
          showAchievement({
            id: badgeId,
            title: `${badge.name} Unlocked!`,
            description: badge.description,
            icon: badge.emoji,
            points: badge.points,
            rarity: badge.rarity.toLowerCase() as BadgeRarityType,
          });
        }
      });
    } catch (error) {
      toast.error("Error checking for new badges");
      console.log("Error: ", error);
    }
  };

  const generateCrowdData = (challenge: any) => {
    if (typeof window === "undefined") {
      return null;
    }
    const participantCount = Math.floor(Math.random() * 50) + 20; // 20-70 participants

    if (challenge.type === "choice") {
      const distributions = challenge.options.map(
        (option: string, index: number) => {
          // Make correct answer slightly less popular (crowd is often wrong!)
          const isCorrect = option === challenge.correctAnswer;
          const baseChance = isCorrect
            ? 0.3
            : 0.7 / (challenge.options.length - 1);
          const votes = Math.floor(
            participantCount * (baseChance + Math.random() * 0.3)
          );
          return {
            option,
            votes,
            percentage: Math.round((votes / participantCount) * 100),
          };
        }
      );

      return {
        participantCount,
        distributions,
        crowdChoice: distributions.reduce((prev: any, current: any) =>
          prev.votes > current.votes ? prev : current
        ).option,
        topReasons: getContextualReasons(challenge),
      };
    } else {
      // For estimates, generate a distribution around the anchor
      const estimates = [];
      const anchor = challenge.anchor || challenge.correctAnswer;

      for (let i = 0; i < participantCount; i++) {
        // Simulate anchoring bias - most estimates cluster around anchor
        const bias = (Math.random() - 0.5) * 0.8 + 1; // 0.6 to 1.4 multiplier
        estimates.push(Math.round(anchor * bias));
      }

      const crowdAverage = Math.round(
        estimates.reduce((a, b) => a + b, 0) / estimates.length
      );

      return {
        participantCount,
        estimates,
        crowdAverage,
        distribution: generateHistogram(estimates),
        topReasons: getContextualReasons(challenge),
      };
    }
  };

  const handleChallengeAnswer = async (challenge: any, answer: any) => {
    try {
      setLoading(true);

      // Generate crowd data when user submits
      const crowd = gameMode === "crowd" ? generateCrowdData(challenge) : null;
      setCrowdData(crowd);

      // Simple scoring logic
      let points = 0;
      let feedback = "";
      let beatCrowd = false;
      let isCorrect = false;

      if (challenge.type === "choice") {
        isCorrect = answer === challenge.correctAnswer;

        const crowdWasWrong =
          gameMode === "crowd" &&
          crowd?.crowdChoice !== challenge.correctAnswer;

        if (isCorrect) {
          points = challenge.points;
          feedback = challenge.explanation;
          if (gameMode === "crowd" && crowdWasWrong) {
            beatCrowd = true;
            points = Math.floor(points * 1.5);
            feedback += `\n\nCROWD BEAT! +50% bonus! Most people chose "${crowd?.crowdChoice}"`;
          }
        } else {
          points = Math.floor(challenge.points * 0.3);
          feedback = challenge.explanation;
        }
      } else if (challenge.type === "estimate") {
        // Convert to number and check if valid
        const userEstimate = Number(answer);
        const correctAnswer = Number(challenge.correctAnswer);

        if (isNaN(userEstimate) || userEstimate <= 0) {
          points = 0;
          feedback = `Please enter a valid number! The correct answer was ${correctAnswer}.`;
        } else {
          const userError =
            Math.abs(userEstimate - correctAnswer) / correctAnswer;
          if (gameMode === "crowd" && crowd?.crowdAverage) {
            const crowdError =
              Math.abs(crowd.crowdAverage - correctAnswer) / correctAnswer;
            beatCrowd = userError < crowdError;
          }

          isCorrect = userError < 0.2;

          const accuracy = 1 - userError;
          points = Math.floor(challenge.points * Math.max(0.1, accuracy));

          if (gameMode === "crowd" && beatCrowd && crowd?.crowdAverage) {
            points = Math.floor(points * 1.3); // Bonus for beating crowd
            feedback = `Your estimate: ${userEstimate}\nCROWD BEAT! (Crowd avg: ${crowd.crowdAverage})\nActual: ${correctAnswer}\n+30% bonus!\n\n${challenge.explanation}`;
          } else if (gameMode === "crowd" && crowd?.crowdAverage) {
            feedback = `Your estimate: ${userEstimate}\nCrowd average: ${
              crowd?.crowdAverage || "N/A"
            }\nActual: ${correctAnswer}\nAccuracy: ${Math.round(
              Math.max(0, accuracy) * 100
            )}%\n\n${challenge.explanation}`;
          }
        }
      }

      // Store result for display
      setLastResult({
        points,
        challengePoints: challenge.points,
        beatCrowd,
      });

      // Store result data for modal
      setResultData({
        points,
        feedback,
        beatCrowd,
        isCorrect,
      });

      // Update score and assign initial rank for first-time players
      if (!isNaN(points) && points > 0) {
        updateUserPointsScore(points);
        await addChallengeActivity(points);
        // Update streak
        const newStreak = streak + (points > challenge.points * 0.7 ? 1 : 0);
        setStreak(newStreak);

        // Update crowd beats

        const newCrowdBeats = beatCrowd ? crowdBeats + 1 : crowdBeats;

        if (beatCrowd) {
          setCrowdBeats(newCrowdBeats);
          // Fire confetti for beating the crowd
          fireConfetti();
        }


        if (user?.id) {
          const result = await updateUserScore(
            user.id,
            points,
            newStreak,
            newCrowdBeats
          );

          if (result.rank) {
            setUserRank(result.rank);
            await updateUserRank(user.id, result.rank);
          }
        }
      }

      if (!completedChallenges.includes(challenge.id)) {
        setCompletedChallenges([...completedChallenges, challenge.id]);
      }
      if (user?.id) {
        try {
          await markChallengeCompleted(
            user.id,
            challenge.id,
            points,
            beatCrowd
          );
        } catch (error) {
          toast.error("Failed to save challenge completion");
          console.error("Failed to save completed challenge:", error);
        }
      }

      setTimeout(() => {
        if (gameMode === "crowd") {
          setShowCrowdResults(true);
        } else {
          setShowResultModal(true);
        }

        setTimeout(() => checkForNewBadges(), 500);
      }, 1000);
    } catch (error) {
      console.log("Error handling challenge answer: ", error);
      toast.error("Error handling challenge answer");
    }
  };

  const handleResultModalClose = () => {
    setShowResultModal(false);
    setResultData(null);
    setShowResult(false);
    setSelectedChallenge(null);
    setLoading(false);
    setUserAnswer("");
  };

  const getFeedbackStyle = () => {
    if (!resultData) return "info";
    if (resultData.isCorrect) return "success";
    return "error";
  };

  return (
    <>
      <AnimatePresence>
        {selectedChallenge && !showCrowdResults && (
          <motion.div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setSelectedChallenge(null);
              setShowCrowdResults(false);
              setCrowdData(null);
              setLastResult(null);
            }}
          >
            <motion.div
              className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 max-w-2xl w-full"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold">
                  {selectedChallenge.title}
                </h3>
                <button
                  onClick={() => {
                    setSelectedChallenge(null);
                    setShowCrowdResults(false);
                    setCrowdData(null);
                    setLastResult(null);
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              </div>

              {gameMode === "crowd" && (
                <div className="mb-4 p-3 bg-brand-teal/20 rounded-xl border border-brand-teal/30">
                  <p className="text-sm text-brand-accent">
                    👥 {Math.floor(Math.random() * 20) + 15} people have already
                    answered this question
                  </p>
                </div>
              )}

              <div className="mb-6">
                <p className="text-lg text-white mb-4">
                  {selectedChallenge.question}
                </p>

                {selectedChallenge.type === "choice" && (
                  <div className="space-y-3">
                    {selectedChallenge.options.map((option: string) => (
                      <button
                        key={option}
                        onClick={() =>
                          handleChallengeAnswer(selectedChallenge, option)
                        }
                        disabled={loading}
                        className="w-full p-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-left transition-all duration-300 disabled:opacity-50"
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                )}

                {selectedChallenge.type === "estimate" && (
                  <div className="space-y-4">
                    <input
                      type="number"
                      value={userAnswer}
                      onChange={(e) => setUserAnswer(e.target.value)}
                      className="w-full p-4 bg-white/5 border border-white/10 rounded-xl text-white text-center text-xl"
                      placeholder="Enter your estimate"
                      disabled={loading}
                    />
                    <button
                      onClick={() =>
                        handleChallengeAnswer(
                          selectedChallenge,
                          userAnswer ? parseInt(userAnswer) : 0
                        )
                      }
                      disabled={
                        loading || !userAnswer || isNaN(Number(userAnswer))
                      }
                      className="w-full bg-brand-teal hover:bg-brand-teal text-white py-3 rounded-xl font-semibold disabled:opacity-50"
                    >
                      {loading ? "Processing..." : "Submit Answer"}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {showResultModal && resultData && (
        <FeedbackModal
          type={getFeedbackStyle() as "success" | "error" | "info"}
          handleContinue={handleResultModalClose}
          showConfetti={
            resultData.beatCrowd ||
            (resultData.isCorrect && resultData.points > 50)
          }
        >
          <div className="text-6xl mb-4">
            {resultData.beatCrowd ? "🔥" : resultData.isCorrect ? "✅" : "❌"}
          </div>

          <h2 className="text-2xl font-bold text-white mb-4">
            {resultData.beatCrowd
              ? "Crowd Beaten!"
              : resultData.isCorrect
              ? "Correct!"
              : resultData.points > 0
              ? "Good Effort!"
              : "Try Again!"}
          </h2>

          <div
            className={`text-xl font-semibold mb-6 ${
              resultData.points > 50
                ? "text-green-400"
                : resultData.points > 0
                ? "text-yellow-400"
                : "text-red-400"
            }`}
          >
            +{resultData.points} points
          </div>

          <div className="text-gray-200 mb-6 leading-relaxed whitespace-pre-line text-sm">
            {resultData.feedback}
          </div>

          {resultData.beatCrowd && (
            <div className="bg-brand-teal/20 rounded-lg p-3 mb-4 border border-brand-teal/30">
              <div className="text-brand-accent text-sm font-semibold">
                🏆 You outsmarted the crowd and earned bonus points!
              </div>
            </div>
          )}
        </FeedbackModal>
      )}
    </>
  );
};
